import { ApiError } from '@/lib/api';
import type { BrowserDraftStatus } from '@/lib/versioned-draft';
import type {
  UsageKind,
  UsageLine,
  VersionTag,
  VersionedError,
  VersionedKind,
  VersionedSort,
  VersionedTagFilter,
  VersionedUsageCount,
} from '@matter_extractor/shared';
import { assertNever, versionedErrorSchema } from '@matter_extractor/shared';
import type { ReactNode } from 'react';

/**
 * The words every screen of the versioning pattern speaks (refonte modulaire
 * §4), and the ONE parameter that keeps them free of a noun.
 *
 * Pure, and one file, for the reason `model-labels.ts` gives at its own head: a
 * refusal worded in two places is worded two ways, and the half that drifts is
 * always the one nobody reads. What is NOT here is any name of an entity or of
 * a version - those come from the base - and any sentence that only makes sense
 * for one kind of object: those live in the object's own labels module
 * (`criterion-labels.ts`) or in the `VersionedWording` it hands to the layer.
 *
 * ## The refusals are worded from the CODE, never from the server's sentence
 *
 * `versionedErrorSchema` carries a closed catalogue of six codes precisely so
 * this file can say six different things (§4). The server's `message` is an
 * English developer string bound for the logs and the OpenAPI; it never reaches
 * an operator. `name-taken` is the one that carries a payload - the other
 * entity's `name` - and it is the reason the catalogue exists rather than an
 * opaque 409: « déjà pris » sends somebody hunting, « Émotion nommée porte déjà
 * ce nom » does not.
 */

/* ------------------------------------------------------------- the wording */

/**
 * What one versioned object is CALLED, everywhere the layer has to name it.
 *
 * The layer is blind to content, which means it is also blind to the noun: it
 * cannot write « Retirer ce critère » without being told the word. So each
 * consumer declares this once - `CRITERION_WORDING` in `criterion-wording.tsx` -
 * and passes it down. Sentences rather than a single noun wherever French
 * grammar would otherwise be templated by hand: an article and an agreement are
 * not a string concatenation, and the day an object is feminine a template
 * would silently read « le expérience ».
 *
 * The two `data-testid` prefixes are here too, and they are load-bearing: every
 * mark of this screen is `${entity}-...` or `${list}-...`, so `criteres.spec.ts`
 * and the capture script keep reading `critere-carte` and `criteres-grille`
 * while a persona list carries `persona-carte` and `personas-grille`.
 */
export interface VersionedWording {
  /**
   * The kind of versioned object this is, as the contract names it: what a gesture that goes
   * through the vitrine says it is about (vitrine.md §4.1, issue #192).
   */
  readonly kind: VersionedKind;
  /** `data-testid` prefix of one entity: « critere » gives `critere-carte`. */
  readonly entity: string;
  /** `data-testid` prefix of the list: « criteres » gives `criteres-grille`. */
  readonly list: string;
  /** The noun as an operator reads it, singular then plural. */
  readonly noun: readonly [string, string];
  /** « Nouveau critère » - the editor's title before the first save. */
  readonly newTitle: string;
  /** « Créer un nouveau critère » - the dashed card of the grid. */
  readonly createLabel: string;
  /**
   * What arms « Enregistrer » on a NEW entity, when the object's condition is
   * not the layer's default (issue #49).
   *
   * The layer says « le nom et la direction », which is the criterion's answer
   * and the persona's. A protocol's is longer - H17 adds the description and the
   * turn cap of every step - and the sentence has to say the whole of it: the
   * grey button is a refusal, and a refusal naming the wrong field sends
   * somebody hunting for one that was never missing. Optional, so the two
   * objects that agree with the default declare nothing.
   */
  readonly saveHintNew?: string;
  /**
   * The line under the title while NO version exists, when the layer's default
   * does not say what the object needs said (issue #148).
   *
   * The default explains what the first save will do. The texts of the noyaux
   * need the other half said: before their v1 there is no entity and so no
   * server draft (N8), and what is typed is kept in the browser instead - the
   * line says so until something is typed, and the browser draft's own line
   * (`draftStateSentence`) takes over once it has been. Optional, so the four
   * objects that came first declare nothing.
   */
  readonly draftStateNew?: string;
  /**
   * The sentence under « Ce que la vN change en développement », drawn once a
   * referent plays the `développement` pointer (issue #155).
   *
   * It is the one sentence of the save box that names WHO plays the object, and
   * the layer cannot know it: a criterion is cited by protocols, a persona is
   * chosen by experiences, a protocol is played by experiences, an experience
   * is played by links (§4, H25). The layer first spoke the criterion's for all
   * four, which named protocols under a list of experiences or of links.
   *
   * Required, and there is no default on purpose. The list it sits under keeps
   * the referents whose OWN development pointer cites the object, while a
   * session resolves each reference at the tag it carries - `production` by
   * default (§4, §5.2) - so « its next sessions will read the vN » is true only
   * over the by-reference lists of the texts of the noyaux. A default would
   * hand that promise to whichever object forgot the field.
   */
  readonly developmentHint: (nextNumber: number) => string;
  /** « Critère « X ». » - what the pointer box names before its sentence. */
  readonly subject: (name: string) => string;
  /** The four sentences of the retirement, each of which names the object. */
  readonly retirement: VersionedRetirementWording;
  /** « Ce critère est retiré. » and « ... un autre critère. » - the two refusals that name it. */
  readonly refusal: VersionedRefusalWording;
}

/**
 * What « Retirer » says, before and after (§4).
 *
 * The two HINTS are nodes and not strings, and the reason is not decoration: a
 * name spliced into a sentence renders as one text node where the prose around
 * it renders as its own, and Chrome measures each node separately - so the two
 * shapes put the tail of the line a third of a pixel apart. A fragment lets the
 * object write its prose with the name in place, exactly as a component would.
 */
export interface VersionedRetirementWording {
  /** « Retirer ce critère » - the panel's title while the entity is live. */
  readonly panelTitle: string;
  /** « Critère retiré » - the panel's title once it is gone. */
  readonly retiredTitle: string;
  /** « Retirer le critère » - the box's confirm button. */
  readonly confirmLabel: string;
  /** The panel's sentence while live, which names what the referents will keep. */
  readonly panelHint: (name: string) => ReactNode;
  /** The panel's sentence once retired, dated. */
  readonly retiredHint: (name: string, day: string) => ReactNode;
  /** « Chacun gardera "remplacer le critère X" » - the box's callout title. */
  readonly warningTitle: (name: string) => string;
}

/** The two of the six refusals of §4 whose sentence names the object. */
export interface VersionedRefusalWording {
  /** `name-taken`, without the other entity's name. */
  readonly nameTaken: string;
  /** `name-taken`, naming it. */
  readonly nameTakenBy: (name: string) => string;
  /** `retired`. */
  readonly retired: string;
}

/* ------------------------------------------------------------------- clocks */

/**
 * « 9 sept. 2026 » - a date, hand-rolled rather than `toLocaleDateString`.
 *
 * The same decision as `chatMinute`, and for the same reason: `Intl` answers
 * from the ICU locale of whatever runs the code, so a test asserting a French
 * month would pass on one machine and print « Sep 9, 2026 » on another, and a
 * capture script would photograph the runner's locale. The screen is French, so
 * the month table is French, and it is the same string everywhere.
 *
 * An unparsable stamp renders empty rather than « Invalid Date » - a bad row is
 * a hole in a sentence, not a word the reader has to decode.
 */
const MONTHS: readonly string[] = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];

export function versionedDay(stamp: string): string {
  /*
    A date-only string is a CALENDAR day and carries no timezone - it is what a
    `<input type="date">` and the two period filters hand over. `new Date(...)`
    parses it as UTC MIDNIGHT while `getDate()` reads local, so west of
    Greenwich the 1st would print as the 31st: the chip would name a day the
    filter is not filtering on. Parsed by hand into a local date instead, and
    the ISO datetimes of a version keep the normal path.
  */
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(stamp);
  const at =
    day === null
      ? new Date(stamp)
      : new Date(Number(day[1] ?? 0), Number(day[2] ?? 1) - 1, Number(day[3] ?? 1));
  if (Number.isNaN(at.getTime())) return '';
  const month = MONTHS[at.getMonth()] ?? '';
  return `${at.getDate()} ${month} ${at.getFullYear()}`;
}

/** « 10:42 » - the hour a draft was autosaved. Local, because a draft is local work. */
export function versionedClock(stamp: string): string {
  const at = new Date(stamp);
  if (Number.isNaN(at.getTime())) return '';
  return `${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`;
}

/* --------------------------------------------------------------------- tags */

/**
 * The two pointers as an operator reads them.
 *
 * The identifiers are English (`development`) because a stored enum is code;
 * the words on the screen are « latest dev » and « latest prod » since the passage
 * goes through the vitrine (vitrine.md §4.5, V4, issue #192): they name what `latest`
 * follows on each side. `versioning.ts` states that split at its head, and this table
 * is the one place it is crossed.
 */
export const VERSION_TAG_LABEL: Readonly<Record<VersionTag, string>> = {
  production: 'latest prod',
  development: 'latest dev',
};

/** The mark of a version that was sent to the prod (vitrine.md §4.5): it never leaves. */
export const IN_PROD_LABEL = 'en prod';

/** How a kind of referent is named in a sentence, singular then plural. */
const KIND_LABEL: Readonly<Record<UsageKind, readonly [string, string]>> = {
  criterion: ['critère', 'critères'],
  persona: ['persona', 'personas'],
  protocol: ['protocole', 'protocoles'],
  experience: ['expérience', 'expériences'],
  // The singleton of issue #148. It cites nothing, so no panel ever names it as
  // a referent; the table is closed over the kinds, and it is spelled all the same.
  'noyau-texts': ['texte des noyaux', 'textes des noyaux'],
  // The two settings singletons of issue #192: a session freezes them and no
  // content cites them, so no panel names them either - spelled all the same.
  models: ['modèles', 'modèles'],
  rules: ['règles', 'règles'],
  // Méthode (issue #196): the fourth settings singleton, frozen by a session and cited by nothing.
  method: ['méthode', 'méthodes'],
  // Not a versioned kind: the referent of an experience is a LINK (§4, H25),
  // and its line reads « Lien « Campagne septembre » » like any other.
  link: ['lien', 'liens'],
};

/** « Protocole » / « Expérience » at the head of a referent line, capitalised. */
export function referentTitle(usage: UsageLine): string {
  const [singular] = KIND_LABEL[usage.kind];
  return `${singular.charAt(0).toUpperCase()}${singular.slice(1)} « ${usage.name} »`;
}

/* -------------------------------------------------------------------- usage */

/**
 * « Utilisé dans 2 protocoles et 1 expérience », or NOTHING (§4).
 *
 * A COUNT and never the list: the card stays scannable and the detail belongs
 * to the editor.
 *
 * ## An empty count is not worded at all
 *
 * « Un usage nul ne s'affiche pas » (§4, tranché à la revue de la maquette
 * Personas): an entity nothing cites yet says nothing - never « utilisé nulle
 * part », never « 0 expérience ». A zero teaches nothing and costs a line on
 * every fresh card, and until the protocol lot lands (§19) that is the state
 * of EVERY card of every library. The empty string is what `versioned-card`
 * drops from its line, separator included.
 *
 * The rule is the card's and the editor panel's, and stops there: the three
 * confirmation boxes keep their « utilisé nulle part encore », which they word
 * themselves through `ReferentRows`.
 *
 * The kinds are walked in the order of `KIND_ORDER` and not in the order the
 * server happened to send: two cards saying the same thing in two orders would
 * read as two different facts.
 */
const KIND_ORDER: readonly VersionedKind[] = ['protocol', 'experience', 'persona', 'criterion'];

export function usageSentence(usage: readonly VersionedUsageCount[]): string {
  const parts = KIND_ORDER.flatMap((kind) => {
    const entry = usage.find((count) => count.kind === kind);
    if (entry === undefined) return [];
    const [singular, plural] = KIND_LABEL[kind];
    return [`${entry.count} ${entry.count === 1 ? singular : plural}`];
  });
  if (parts.length === 0) return '';
  // « a et b » with no comma: there are at most four kinds and the sentence is
  // read at a glance on a card, so a serial list would be longer than the fact.
  const last = parts.at(-1) ?? '';
  const head = parts.slice(0, -1);
  return `Utilisé dans ${head.length === 0 ? last : `${head.join(', ')} et ${last}`}`;
}

/* ------------------------------------------------------------------ filters */

export const TAG_FILTER_LABEL: Readonly<Record<VersionedTagFilter, string>> = {
  all: 'Tous',
  'in-production': 'Avec un latest prod',
  'never-production': 'Jamais passé en production',
};

/**
 * The four common sorts of §4. Each object spreads this and adds its own.
 *
 * Written in the order of `versionedSortSchema` for the reader only: the
 * select lists the schema's `options`, never the keys of this record.
 */
export const VERSIONED_SORT_LABEL: Readonly<Record<VersionedSort, string>> = {
  name: 'Nom',
  'last-version': 'Dernière version',
  created: 'Création',
  usage: 'Utilisations',
};

/**
 * The sentinel the referent filters carry in this lot, and why it is the only
 * value they carry (§4, §19).
 *
 * §4 asks the referent filter to offer the referents themselves plus « nulle
 * part ». A CARD, though, says usage as a count per kind - it names no referent
 * - and no route serves the referent list yet: protocols are lot 4 and
 * experiences lot 5, so today nothing cites any object at all. Offering a name
 * the page cannot resolve would be a filter that silently empties the list,
 * which is worse than a filter that admits it has two states.
 *
 * So the select offers « Tous » and « Nulle part », the filter schema keeps its
 * `string | null` field untouched, and the URL reader drops any other value.
 * The day a protocol exists, the options grow and this constant stops being the
 * whole vocabulary.
 */
export const REFERENT_NOWHERE = 'nowhere';

export const REFERENT_FILTER_LABEL: Readonly<Record<'protocol' | 'experience', string>> = {
  protocol: 'Protocole',
  experience: 'Expérience',
};

/* ------------------------------------------------------------- draft state */

/**
 * The line under the editor's title: what state the draft is in (§4).
 *
 * §4 asks the editor to display « qui a modifié le brouillon et quand » at open
 * time, and the maquette adds the local stamp beside it - « Brouillon
 * autosauvegardé à 10:42 · modifié par alice.pesty à 10:40 ». Two stamps and
 * not one, because they answer two questions: the first is « did my typing
 * survive », the second is « was somebody else here ». A single stamp would
 * conflate them, and the whole point of the optimistic control of §4 is that
 * the second case exists.
 *
 * Pure, and here rather than in the header component, because the three states
 * it distinguishes are the three the screen is asserted on: an entity that does
 * not exist yet, a draft identical to the `développement` version - which is
 * exactly when the save button is inactive - and a draft that differs.
 */
export function draftStateSentence(input: {
  /** No entity yet: the saisie lives only in the page (§6). */
  readonly isNew: boolean;
  /** When THIS editor last autosaved, or null before its first autosave. */
  readonly autosavedAt: string | null;
  /** What the server holds, or null when it holds no draft. */
  readonly serverDraft: { readonly updatedAt: string; readonly updatedBy: string } | null;
  /** True when the draft is structurally identical to the `développement` version. */
  readonly identical: boolean;
  readonly developmentNumber: number;
  /** The object's own new-entity line, when the default does not describe it (#148). */
  readonly draftStateNew?: string;
  /**
   * Where a new entity's browser draft stands, for the object that keeps one
   * (review of !462) - null while nothing has been written yet.
   */
  readonly browserDraft?: BrowserDraftStatus | null;
}): string {
  if (input.isNew) {
    const kept = input.browserDraft ?? null;
    // Said when something WAS written, and only then: the time is the proof
    // that the typing survives a reload, the refusal that it will not.
    if (kept?.kind === 'kept')
      return `Brouillon gardé dans ce navigateur à ${versionedClock(kept.at)}`;
    if (kept?.kind === 'restored') {
      return `Brouillon repris de ce navigateur, gardé le ${versionedDay(kept.at)} à ${versionedClock(kept.at)}`;
    }
    if (kept?.kind === 'refused') {
      return 'Ce navigateur refuse de garder le brouillon : la saisie ne vit que dans cette page.';
    }
    return (
      input.draftStateNew ??
      'Aucune version encore. Le premier enregistrement écrit la v1 et pose latest dev dessus ; latest prod attend votre geste.'
    );
  }
  if (input.identical) return `Brouillon identique à la v${input.developmentNumber}`;
  const local =
    input.autosavedAt === null
      ? 'Brouillon en cours'
      : `Brouillon autosauvegardé à ${versionedClock(input.autosavedAt)}`;
  if (input.serverDraft === null) return local;
  const { updatedBy, updatedAt } = input.serverDraft;
  return `${local} · modifié par ${updatedBy} à ${versionedClock(updatedAt)}`;
}

/**
 * The sentence under « Enregistrer une nouvelle version »: why it is on, or
 * why it is off (§4, §6).
 *
 * The button being grey is a REFUSAL, and a refusal with no sentence is a
 * screen that looks broken. `Button`'s own `DISABLED_INERT` recipe exists for
 * the same reason and says so at length: a disabled control has to stay
 * readable precisely when somebody needs to read why it will not answer.
 */
export function saveHintSentence(input: {
  readonly isNew: boolean;
  readonly nextNumber: number;
  readonly developmentNumber: number;
  /** The object's own condition, when the default does not describe it (#49). */
  readonly saveHintNew?: string;
}): string {
  if (input.isNew) {
    return input.saveHintNew ?? 'Actif dès que le nom et la direction sont remplis.';
  }
  return `Crée la v${input.nextNumber}. Inactif tant que rien n’a changé depuis la v${input.developmentNumber}.`;
}

/* ---------------------------------------------------------- discarding a draft */

/**
 * The button of the head and the confirm button of its box: one gesture, one
 * label (issue #185, refonte modulaire §4). The same words on both, so the
 * operator who clicked « Abandonner le brouillon » confirms the very thing
 * they asked for and not a paraphrase of it.
 */
export const DISCARD_DRAFT_LABEL = 'Abandonner le brouillon';

/**
 * What the box says it is about to do (issue #185): WHO changed the draft and
 * WHEN, and where the editor will start again from.
 *
 * Both stamps are the server draft's own, read when the editor opened or when
 * its last autosave landed - not « now »: the question the operator is
 * answering is « am I about to throw away somebody else's work », and the
 * author and the day are what answer it. The destination is named by its
 * number, `développement`, which is the version the editor opens on when there
 * is no draft (`VersionedDraftKit.opening`).
 */
export function discardDraftDescription(input: {
  readonly updatedBy: string;
  readonly updatedAt: string;
  readonly developmentNumber: number;
}): string {
  const { updatedBy, updatedAt, developmentNumber } = input;
  return `Le brouillon a été modifié par ${updatedBy} le ${versionedDay(updatedAt)} à ${versionedClock(updatedAt)}. Il est supprimé du serveur, et l’éditeur repartira de la version développement (v${developmentNumber}).`;
}

/** The one sentence of the box that says what the gesture does NOT touch. */
export const DISCARD_DRAFT_REASSURANCE =
  'Les versions enregistrées et les pointeurs ne bougent pas : seul le brouillon est supprimé, et cette saisie ne pourra pas être retrouvée.';

/* ---------------------------------------------------------------- refusals */

/**
 * What each refusal of the pattern says on the screen of ONE object (§4).
 *
 * Four of the six sentences hold for every versioned object and are written
 * here; the two that name the object - `name-taken` and `retired` - come from
 * its own wording. `name` is the other entity on `name-taken`, and the sentence
 * NAMES it: the unicity is among the entities that are not retired, so the
 * collision can be with something the operator cannot see in the list.
 */
export function versionedRefusal(error: VersionedError, wording: VersionedRefusalWording): string {
  switch (error.code) {
    case 'name-taken':
      return error.name === undefined || error.name === ''
        ? wording.nameTaken
        : wording.nameTakenBy(error.name);
    case 'draft-stale':
      return 'Quelqu’un a modifié ce brouillon depuis que cet écran l’a lu. Rechargez le brouillon du serveur : votre dernière saisie n’a pas été enregistrée.';
    case 'no-change':
      return 'Rien n’a changé depuis la version latest dev : il n’y a pas de nouvelle version à enregistrer.';
    case 'retired':
      return wording.retired;
    case 'pointer-empty':
      return 'Ce pointeur ne désigne aucune version.';
    case 'version-unknown':
      return 'Cette version n’existe plus. Rechargez l’écran pour relire l’historique.';
    default:
      return assertNever(error.code, 'versionedRefusal');
  }
}

/**
 * The refusal inside a failed request, or `null` when there was none.
 *
 * `ApiError.details` is kept unparsed by `lib/api.ts` on purpose (see its
 * head), so the parse happens here - once - and a body that is not one of the
 * six codes reads as « no typed refusal » rather than crashing the screen. The
 * caller then falls back to its own generic sentence, which is the honest
 * answer for a 500.
 */
export function versionedRefusalOf(error: unknown): VersionedError | null {
  if (!(error instanceof ApiError)) return null;
  const parsed = versionedErrorSchema.safeParse(error.details);
  return parsed.success ? parsed.data : null;
}

/** True when a failed write is the one refusal the editor answers with a reload button. */
export function isDraftStale(error: unknown): boolean {
  return versionedRefusalOf(error)?.code === 'draft-stale';
}
